import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { TIPO_CLIENTE } from "../catalogo";

const fmt = (n) => Number(n).toFixed(2).replace(".", ",") + " €";

const STATI = ["bozza", "inviato", "accettato", "rifiutato", "scaduto"];
const STATO_COLOR = {
  bozza:     { bg: "#f1f5f9", color: "#64748b" },
  inviato:   { bg: "#eff6ff", color: "#1d4ed8" },
  accettato: { bg: "#f0fdf4", color: "#15803d" },
  rifiutato: { bg: "#fef2f2", color: "#dc2626" },
  scaduto:   { bg: "#fefce8", color: "#92400e" },
};

function StatoBadge({ stato }) {
  const s = STATO_COLOR[stato] || { bg: "#f1f5f9", color: "#64748b" };
  return <span className="stato-badge" style={{ background: s.bg, color: s.color }}>{stato}</span>;
}

function TipoBadge({ tipo }) {
  const t = TIPO_CLIENTE[tipo];
  if (!t) return <span>{tipo}</span>;
  return <span className="tipo-badge-sm" style={{ background: t.bg, color: t.color }}>{t.short}</span>;
}

export default function Archivio({ onNuovo }) {
  const [lista, setLista]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore]   = useState(null);
  const [filtri, setFiltri]   = useState({ tipo: "", stato: "", anno: "", q: "" });
  const [dettaglio, setDettaglio] = useState(null);
  const [loadingDet, setLoadingDet] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true); setErrore(null);
    try {
      const params = {};
      if (filtri.tipo)  params.tipo  = filtri.tipo;
      if (filtri.stato) params.stato = filtri.stato;
      if (filtri.anno)  params.anno  = filtri.anno;
      if (filtri.q)     params.q     = filtri.q;
      setLista(await api.lista(params));
    } catch (e) {
      setErrore(e.message);
    }
    setLoading(false);
  }, [filtri]);

  useEffect(() => { carica(); }, [carica]);

  const apriDettaglio = async (id) => {
    setLoadingDet(true);
    try { setDettaglio(await api.dettaglio(id)); }
    catch (e) { alert(e.message); }
    setLoadingDet(false);
  };

  const cambiaStato = async (id, stato) => {
    try {
      await api.aggiornaStato(id, stato);
      carica();
      if (dettaglio?.id === id) setDettaglio({ ...dettaglio, stato });
    } catch (e) { alert(e.message); }
  };

  const elimina = async (id) => {
    if (!window.confirm("Eliminare questo preventivo?")) return;
    try { await api.elimina(id); carica(); setDettaglio(null); }
    catch (e) { alert(e.message); }
  };

  // ── Dettaglio modal ────────────────────────────────────────────────────────
  if (dettaglio) {
    const tipo = TIPO_CLIENTE[dettaglio.tipo_cliente];
    return (
      <div className="page">
        <div className="page-header">
          <button className="btn btn-ghost" onClick={() => setDettaglio(null)}>← Archivio</button>
          <h1 style={{ fontFamily: "monospace" }}>{dettaglio.protocollo}</h1>
          <StatoBadge stato={dettaglio.stato} />
        </div>

        <div className="card">
          <div className="det-header" style={{ borderColor: tipo?.color }}>
            <div>
              <div className="det-nome">{dettaglio.ragione_sociale}</div>
              <div className="det-sub">{[dettaglio.email, dettaglio.telefono].filter(Boolean).join(" · ")}</div>
              {dettaglio.referente && <div className="det-sub">Ref: {dettaglio.referente}</div>}
              {dettaglio.piva && <div className="det-sub">P.IVA: {dettaglio.piva}</div>}
            </div>
            <div style={{ textAlign: "right" }}>
              <TipoBadge tipo={dettaglio.tipo_cliente} />
              <div className="det-totale">{fmt(dettaglio.totale)}</div>
              <div className="det-sub">{new Date(dettaglio.created_at).toLocaleDateString("it-IT")}</div>
            </div>
          </div>

          <table className="riepilogo-table" style={{ marginTop: 16 }}>
            <thead><tr style={{ background: tipo?.color || "#0f2850" }}>
              <th>Servizio</th><th>Modalità</th><th>Q.tà</th><th>P.U.</th><th>Totale</th>
            </tr></thead>
            <tbody>
              {dettaglio.voci.map((v, i) => (
                <tr key={i} className={i % 2 === 0 ? "row-even" : "row-odd"}>
                  <td>{v.nome}{v.custom && <span className="badge-c">custom</span>}</td>
                  <td>{v.modalita === "noleggio" ? "Noleggio/mese" : v.unita === "mese" ? "Canone/mese" : "Una tantum"}</td>
                  <td style={{ textAlign: "center" }}>{v.qty}</td>
                  <td style={{ textAlign: "right" }}>{fmt(v.prezzo)}</td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(v.prezzo * v.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="totali-box">
            <div className="tot-row"><span>Subtotale</span><span>{fmt(dettaglio.subtotale)}</span></div>
            {dettaglio.sconto_pct > 0 && <div className="tot-row sconto"><span>Sconto {dettaglio.sconto_pct}%</span><span>- {fmt(dettaglio.sconto_amt)}</span></div>}
            <div className="tot-row"><span>Imponibile</span><span>{fmt(dettaglio.imponibile)}</span></div>
            <div className="tot-row"><span>IVA 22%</span><span>{fmt(dettaglio.iva)}</span></div>
            <div className="tot-finale" style={{ background: tipo?.color || "#0f2850" }}>
              <span>TOTALE</span><span>{fmt(dettaglio.totale)}</span>
            </div>
          </div>

          {dettaglio.note && <div className="note-preview">📝 {dettaglio.note}</div>}

          <div className="det-actions">
            <div>
              <span className="form-label">Cambia stato: </span>
              {STATI.map(s => (
                <button key={s} className={`chip ${dettaglio.stato === s ? "active" : ""}`}
                  onClick={() => cambiaStato(dettaglio.id, s)}>{s}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" onClick={() => api.downloadPdf(dettaglio.id, dettaglio.protocollo).catch(e => alert(e.message))}>⬇️ PDF</button>
              <button className="btn btn-danger" onClick={() => elimina(dettaglio.id)}>🗑 Elimina</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Lista ──────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      <div className="page-header">
        <h1>Archivio preventivi</h1>
        <button className="btn btn-primary" onClick={onNuovo}>+ Nuovo</button>
      </div>

      {/* Filtri */}
      <div className="card filtri-bar">
        <input className="form-input" placeholder="🔍 Cerca cliente, protocollo..."
          value={filtri.q} onChange={e => setFiltri({ ...filtri, q: e.target.value })} style={{ flex: 2 }} />
        <select className="form-input" value={filtri.tipo} onChange={e => setFiltri({ ...filtri, tipo: e.target.value })}>
          <option value="">Tutti i tipi</option>
          {Object.entries(TIPO_CLIENTE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="form-input" value={filtri.stato} onChange={e => setFiltri({ ...filtri, stato: e.target.value })}>
          <option value="">Tutti gli stati</option>
          {STATI.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-input" value={filtri.anno} onChange={e => setFiltri({ ...filtri, anno: e.target.value })}>
          <option value="">Tutti gli anni</option>
          {[2026, 2025, 2024].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {loading && <div className="loading">Caricamento...</div>}
      {errore  && <div className="errore-box">⚠️ {errore}</div>}

      {!loading && !errore && lista.length === 0 && (
        <div className="empty-state">
          <div style={{ fontSize: 48 }}>📄</div>
          <div>Nessun preventivo trovato</div>
          <button className="btn btn-primary" onClick={onNuovo}>Crea il primo</button>
        </div>
      )}

      {!loading && lista.length > 0 && (
        <div className="lista-preventivi">
          {lista.map(p => (
            <div key={p.id} className="preventivo-row" onClick={() => apriDettaglio(p.id)}>
              <div className="pr-proto">{p.protocollo}</div>
              <TipoBadge tipo={p.tipo_cliente} />
              <div className="pr-nome">{p.ragione_sociale}</div>
              <StatoBadge stato={p.stato} />
              <div className="pr-totale">{fmt(p.totale)}</div>
              <div className="pr-data">{new Date(p.created_at).toLocaleDateString("it-IT")}</div>
              <div className="pr-actions" onClick={e => e.stopPropagation()}>
                <button className="btn-icon" title="Scarica PDF" onClick={(e) => { e.stopPropagation(); api.downloadPdf(p.id, p.protocollo).catch(e => alert(e.message)); }}>⬇️</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {loadingDet && <div className="loading-overlay">Caricamento dettaglio...</div>}
    </div>
  );
}
